import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords do not match");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success(data.message || "Password reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <div className="text-center max-w-sm" data-testid="reset-invalid">
          <p className="overline-label">Password reset</p>
          <h2 className="text-2xl font-black tracking-tight mt-1 mb-3">Invalid link</h2>
          <p className="text-sm text-muted-foreground mb-6">This reset link is missing or malformed. Please request a new one from the sign-in page.</p>
          <Link to="/login" data-testid="goto-login-link" className="text-primary font-semibold hover:underline">Back to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <img src="/nucleus-logo.png" alt="" className="h-10 w-10 object-contain" />
          <span className="font-bold text-lg tracking-tight">Neuclius</span>
        </div>

        {done ? (
          <div data-testid="reset-success" className="space-y-5">
            <CheckCircle size={40} weight="fill" className="text-emerald-600" />
            <h2 className="text-2xl font-black tracking-tight">Password updated</h2>
            <p className="text-sm text-muted-foreground">Your password has been reset. You can now sign in with your new password.</p>
            <Button onClick={() => navigate("/login", { replace: true })} data-testid="reset-goto-login" className="w-full rounded-sm bg-primary hover:bg-primary/90 h-10 font-semibold">
              Go to sign in <ArrowRight size={16} className="ml-2" />
            </Button>
          </div>
        ) : (
          <>
            <p className="overline-label">Password reset</p>
            <h2 className="text-2xl font-black tracking-tight mt-1 mb-6">Choose a new password</h2>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="overline-label" htmlFor="new-password">New password</Label>
                <Input id="new-password" type="password" required data-testid="reset-password-input" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 rounded-sm" placeholder="••••••••" />
              </div>
              <div>
                <Label className="overline-label" htmlFor="confirm-password">Confirm password</Label>
                <Input id="confirm-password" type="password" required data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-sm" placeholder="••••••••" />
              </div>
              <Button type="submit" disabled={loading} data-testid="reset-submit-button" className="w-full rounded-sm bg-primary hover:bg-primary/90 h-10 font-semibold group">
                {loading ? "Saving…" : "Reset password"}
                <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-6 text-center">
              <Link to="/login" data-testid="reset-back-link" className="text-primary font-semibold hover:underline">Back to sign in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
